<template>
  <div class="grid grid-cols-1 gap-4 md:grid-cols-2 md:items-start md:gap-7">
    <section class="grid gap-5">
      <div class="grid gap-3.5 py-2.5">
        <h3 class="text-[13px] font-medium uppercase tracking-[1px] text-[#ffaa00]">Basic settings</h3>
        <bly-native-text-field
          :value="model.name"
          label="Name"
          :error="errors.name"
          required
          @input="updateRoot('name', $event)"
        />
        <bly-native-textarea-field
          :value="model.description"
          label="Description"
          @input="updateRoot('description', $event)"
        />
        <bly-native-text-field
          v-if="mode === 'create'"
          :value="model.id"
          label="ID"
          placeholder="Optional custom ID"
          @input="updateRoot('id', $event)"
        />
        <bly-native-select-field
          v-if="mode === 'create'"
          :value="model.accountIds"
          label="Accounts"
          hint="Accounts are only mapped during creation, matching the existing behavior."
          :error="errors.accountIds"
          :options="accountOptions"
          multiple
          @input="updateRoot('accountIds', $event)"
        />
      </div>

      <div class="grid gap-3.5 py-2.5">
        <h3 class="text-[13px] font-medium uppercase tracking-[1px] text-[#ffaa00]">Claims</h3>
        <div class="grid gap-1">
          <span class="text-xs font-semibold text-[#dde6f3]">Access claims *</span>
          <el-cascader
            :value="model.claimsCanAccess"
            :options="claimsTree"
            :props="{ multiple: true, checkStrictly: true }"
            :show-all-levels="false"
            collapse-tags
            clearable
            class="w-full"
            @input="updateRoot('claimsCanAccess', $event)"
          />
          <span v-if="errors.claimsCanAccess" class="text-xs text-red-300">
            {{ errors.claimsCanAccess }}
          </span>
        </div>

        <div class="mt-3.5 grid gap-1">
          <span class="text-xs font-semibold text-[#dde6f3]">Edit claims *</span>
          <el-cascader
            :value="model.claimsCanEdit"
            :options="claimsTree"
            :props="{ multiple: true, checkStrictly: true }"
            :show-all-levels="false"
            collapse-tags
            clearable
            class="w-full"
            @input="updateRoot('claimsCanEdit', $event)"
          />
          <span v-if="errors.claimsCanEdit" class="text-xs text-red-300">
            {{ errors.claimsCanEdit }}
          </span>
        </div>
      </div>
    </section>

    <section class="grid gap-5">
      <div class="grid gap-3.5 py-2.5">
        <h3 class="text-[13px] font-medium uppercase tracking-[1px] text-[#ffaa00]">Output configuration</h3>

        <bly-native-text-field
          :value="model.output.displayName"
          label="Display name"
          @input="updateOutput('displayName', $event)"
        />

        <bly-permission-field label="Output type" :error="errors['output.outputType']" required>
          <bly-pill-selector
            :value="model.output.outputType"
            label="Output type"
            :options="outputTypeOptions"
            @input="$emit('type-change', $event)"
          />
        </bly-permission-field>

        <bly-native-select-field
          :value="model.output.status"
          label="Status"
          :error="errors['output.status']"
          :options="statusOptions"
          placeholder="Select status"
          required
          @input="updateOutput('status', $event)"
        />

        <bly-native-checkbox-field
          v-if="isEventEditor"
          :value="!!model.output.allowExternalConfigUpdate"
          label="Allow external config update"
          @input="updateOutput('allowExternalConfigUpdate', $event)"
        />

        <bly-native-select-field
          v-if="locationOptions.length > 0"
          :value="model.output.location"
          label="Location"
          :error="errors['output.location']"
          :options="locationOptions"
          placeholder="Select location"
          @input="updateOutput('location', $event)"
        />

        <bly-native-text-field
          v-if="['ROUTE', 'TECHEX'].includes(model.output.outputType)"
          :value="model.output.url"
          label="URL"
          @input="updateOutput('url', $event)"
        />
      </div>

      <div v-if="model.output.outputType === 'OTT'" class="grid gap-3.5 py-2.5">
        <h3 class="text-[13px] font-medium uppercase tracking-[1px] text-[#ffaa00]">Wowza</h3>
        <bly-native-select-field
          :value="model.output.wowzaServerId"
          label="Wowza server"
          :error="errors['output.wowzaServerId']"
          :options="wowzaServerOptions"
          placeholder="Select Wowza server"
          @input="$emit('wowza-server-change', $event)"
        />
        <bly-native-select-field
          :value="model.output.wowzaApplicationId"
          label="Wowza application"
          :error="errors['output.wowzaApplicationId']"
          :options="wowzaApplicationOptions"
          placeholder="Select Wowza application"
          @input="$emit('wowza-application-change', $event)"
        />
        <bly-native-select-field
          :value="model.output.ottPlatform"
          label="OTT platform"
          :error="errors['output.ottPlatform']"
          :options="ottPlatformOptions"
          placeholder="Select OTT platform"
          @input="updateOutput('ottPlatform', $event)"
        />
        <bly-native-select-field
          :value="model.output.ottDefaultTranscoderProfile"
          label="Default transcoder profile"
          :options="transcoderTemplateOptions"
          placeholder="Select transcoder profile"
          @input="updateOutput('ottDefaultTranscoderProfile', $event)"
        />
        <bly-native-checkbox-field
          :value="!!model.output.ottNoStremKeyRequired"
          label="Stream key not required"
          @input="updateOutput('ottNoStremKeyRequired', $event)"
        />

        <div v-if="model.output.allowExternalConfigUpdate" class="grid gap-3">
          <bly-native-checkbox-field
            :value="!!model.output.createWowzaTarget"
            label="Create new target"
            @input="updateOutput('createWowzaTarget', $event)"
          />
          <bly-native-select-field
            v-if="!model.output.createWowzaTarget"
            :value="model.output.wowzaTargetId"
            label="Wowza target"
            :options="wowzaTargetOptions"
            placeholder="Select target"
            @input="$emit('wowza-target-change', $event)"
          />
          <wowza-target-config
            v-if="model.output.createWowzaTarget || model.output.wowzaTargetId"
            :value="model.output.wowzaTargetConfig"
            @input="updateOutput('wowzaTargetConfig', $event)"
          />
          <div v-if="errors['output.wowzaTargetConfig.entryName']" class="text-xs text-red-300">
            {{ errors['output.wowzaTargetConfig.entryName'] }}
          </div>
          <div v-if="errors['output.wowzaTargetConfig.host']" class="text-xs text-red-300">
            {{ errors['output.wowzaTargetConfig.host'] }}
          </div>
        </div>
      </div>

      <div v-if="model.output.outputType === 'ROUTE'" class="grid gap-3.5 py-2.5">
        <h3 class="text-[13px] font-medium uppercase tracking-[1px] text-[#ffaa00]">Stream Router</h3>
        <bly-native-select-field
          :value="model.output.streamRouterId"
          label="Stream Router"
          :error="errors['output.streamRouterId']"
          :options="streamRouterOptions"
          placeholder="Select Stream Router"
          required
          @input="$emit('stream-router-change', $event)"
        />
        <bly-native-select-field
          :value="model.output.interfaceId"
          label="Interface"
          :error="errors['output.interfaceId']"
          :options="interfaceOptions"
          placeholder="Select interface"
          required
          @input="$emit('interface-change', $event)"
        />
        <bly-native-text-field
          :value="model.output.interfaceName"
          label="Interface name"
          readonly
        />
        <bly-native-text-field
          :value="model.output.interfaceDescription"
          label="Interface description"
          readonly
        />
        <div
          v-if="model.output.routeInterfaceConflict"
          class="mt-3 rounded-lg bg-[#ffaa00]/10 px-3 py-2 text-xs text-[#ffbf3f]"
        >
          Interface is already used by <strong>{{ model.output.routeInterfaceConflict.name }}</strong>
          ({{ model.output.routeInterfaceConflict.id }}).
        </div>

      </div>

      <div v-if="model.output.outputType === 'TECHEX'" class="grid gap-3.5 py-2.5">
        <h3 class="text-[13px] font-medium uppercase tracking-[1px] text-[#ffaa00]">Techex</h3>
        <bly-native-text-field
          :value="model.output.techexOutputId"
          label="Techex Output ID"
          :error="errors['output.techexOutputId']"
          @input="updateOutput('techexOutputId', $event)"
        />
        <bly-native-text-field
          :value="model.output.techexEdgeId"
          label="Techex Edge ID"
          :error="errors['output.techexEdgeId']"
          @input="updateOutput('techexEdgeId', $event)"
        />
        <bly-native-text-field
          :value="model.output.techexBlankStreamId"
          label="Techex Blank Stream ID"
          @input="updateOutput('techexBlankStreamId', $event)"
        />
      </div>

      <div v-if="model.output.outputType === 'MEDIA_CONNECT'" class="grid gap-3.5 py-2.5">
        <h3 class="text-[13px] font-medium uppercase tracking-[1px] text-[#ffaa00]">MediaConnect</h3>
        <bly-native-select-field
          :value="model.output.mediaConnectOutputId"
          label="Existing output"
          :options="mediaConnectOutputOptions"
          placeholder="Select existing MediaConnect output"
          @input="$emit('media-connect-output-change', $event)"
        />
        <bly-native-select-field
          :value="model.output.routingScope"
          label="Routing scope"
          :error="errors['output.routingScope']"
          :options="routingScopeOptions"
          @input="updateOutput('routingScope', $event)"
        />
        <bly-native-text-field
          :value="model.output.routerOutputArn"
          label="Router Output ARN"
          @input="updateOutput('routerOutputArn', $event)"
        />
        <div v-if="model.output.allowExternalConfigUpdate" class="grid gap-3">
          <bly-native-text-field
            :value="model.output.mcOutputName"
            label="MediaConnect name"
            :error="errors['output.mcOutputName']"
            @input="updateOutput('mcOutputName', $event)"
          />
          <bly-native-text-field
            :value="model.output.mcOutputBitrate"
            type="number"
            label="Maximum bitrate"
            :error="errors['output.mcOutputBitrate']"
            @input="updateOutput('mcOutputBitrate', $event)"
          />
          <bly-native-select-field
            :value="model.output.mcOutputTier"
            label="Tier"
            :error="errors['output.mcOutputTier']"
            :options="tierOutputOptions"
            @input="updateOutput('mcOutputTier', $event)"
          />
          <bly-native-select-field
            :value="model.output.mcOutputNetworkInterfaceArn"
            label="Network interface"
            :error="errors['output.mcOutputNetworkInterfaceArn']"
            :options="networkInterfaceOptions"
            @input="updateOutput('mcOutputNetworkInterfaceArn', $event)"
          />
          <bly-native-select-field
            :value="model.output.mcOutputProtocol"
            label="Protocol"
            :error="errors['output.mcOutputProtocol']"
            :options="protocolOptions"
            @input="updateOutput('mcOutputProtocol', $event)"
          />

          <template v-if="model.output.mcOutputProtocol === 'SRT_CALLER'">
            <bly-native-text-field
              :value="model.output.mcOutputDestinationAddress"
              label="Destination address"
              :error="errors['output.mcOutputDestinationAddress']"
              @input="updateOutput('mcOutputDestinationAddress', $event)"
            />
            <bly-native-text-field
              :value="model.output.mcOutputDestinationPort"
              type="number"
              label="Destination port"
              :error="errors['output.mcOutputDestinationPort']"
              @input="updateOutput('mcOutputDestinationPort', $event)"
            />
            <bly-native-text-field
              :value="model.output.mcOutputLatency"
              type="number"
              label="Latency (ms)"
              :error="errors['output.mcOutputLatency']"
              @input="updateOutput('mcOutputLatency', $event)"
            />
          </template>

          <template v-if="model.output.mcOutputProtocol === 'SRT_LISTENER'">
            <bly-native-text-field
              :value="model.output.mcOutputPort"
              type="number"
              label="Port"
              :error="errors['output.mcOutputPort']"
              @input="updateOutput('mcOutputPort', $event)"
            />
            <bly-native-text-field
              :value="model.output.mcOutputLatency"
              type="number"
              label="Latency (ms)"
              :error="errors['output.mcOutputLatency']"
              @input="updateOutput('mcOutputLatency', $event)"
            />
          </template>

          <template v-if="['RTP', 'RIST'].includes(model.output.mcOutputProtocol)">
            <bly-native-text-field
              :value="model.output.mcOutputDestinationAddress"
              label="Destination address"
              :error="errors['output.mcOutputDestinationAddress']"
              @input="updateOutput('mcOutputDestinationAddress', $event)"
            />
            <bly-native-text-field
              :value="model.output.mcOutputDestinationPort"
              type="number"
              label="Destination port"
              :error="errors['output.mcOutputDestinationPort']"
              @input="updateOutput('mcOutputDestinationPort', $event)"
            />
          </template>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import WowzaTargetConfig from "@/components/Wowza/WowzaTargetConfig.vue";

export default {
  name: "OutputPermissionForm",
  components: {
    WowzaTargetConfig,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
    mode: {
      type: String,
      default: "create",
    },
    accounts: {
      type: Array,
      default: () => [],
    },
    claimsTree: {
      type: Array,
      default: () => [],
    },
    outputTypes: {
      type: Array,
      default: () => [],
    },
    status: {
      type: Array,
      default: () => [],
    },
    locationOptions: {
      type: Array,
      default: () => [],
    },
    streamRouters: {
      type: Array,
      default: () => [],
    },
    streamRouterInterfaces: {
      type: Array,
      default: () => [],
    },
    availableWowzaServers: {
      type: Array,
      default: () => [],
    },
    wowzaApplications: {
      type: Array,
      default: () => [],
    },
    wowzaTargets: {
      type: Array,
      default: () => [],
    },
    transcoderTemplates: {
      type: Array,
      default: () => [],
    },
    ottPlatformTypes: {
      type: Array,
      default: () => [],
    },
    mediaConnectRoutingScopes: {
      type: Array,
      default: () => [],
    },
    mediaConnectTiersOutput: {
      type: Array,
      default: () => [],
    },
    mediaConnectProtocols: {
      type: Array,
      default: () => [],
    },
    mediaConnectNetworkInterfaces: {
      type: Array,
      default: () => [],
    },
    mediaConnectOutputs: {
      type: Array,
      default: () => [],
    },
    isEventEditor: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    model() {
      return this.value;
    },
    accountOptions() {
      return this.accounts.map((account) => ({
        value: account.id,
        label: `${account.client.name} > ${account.name}`,
      }));
    },
    outputTypeOptions() {
      return this.outputTypes.map((type) => ({ value: type, label: type.replace("_", " ") }));
    },
    statusOptions() {
      return this.status.map((statusItem) => ({ value: statusItem, label: statusItem }));
    },
    streamRouterOptions() {
      return this.streamRouters.map((router) => ({ value: router.id, label: router.name }));
    },
    interfaceOptions() {
      return this.streamRouterInterfaces.map((routerInterface) => ({
        value: routerInterface.id,
        label: routerInterface.name || routerInterface.id,
      }));
    },
    wowzaServerOptions() {
      return this.availableWowzaServers.map((server) => ({ value: server.id, label: server.name }));
    },
    wowzaApplicationOptions() {
      return this.wowzaApplications.map((app) => ({ value: app.id, label: app.name }));
    },
    wowzaTargetOptions() {
      return this.wowzaTargets.map((target) => ({
        value: target.id,
        label: target.name || target.entryName,
      }));
    },
    transcoderTemplateOptions() {
      return this.transcoderTemplates.map((template) => ({
        value: template.id,
        label: template.name,
      }));
    },
    ottPlatformOptions() {
      return this.ottPlatformTypes.map((platform) => ({ value: platform, label: platform }));
    },
    routingScopeOptions() {
      return this.mediaConnectRoutingScopes.map((scope) => ({ value: scope, label: scope }));
    },
    tierOutputOptions() {
      return this.mediaConnectTiersOutput.map((tier) => ({ value: tier, label: tier }));
    },
    protocolOptions() {
      return this.mediaConnectProtocols.map((protocol) => ({ value: protocol, label: protocol }));
    },
    networkInterfaceOptions() {
      const selectedRegion = this.model?.output?.location || null;
      return this.mediaConnectNetworkInterfaces
        .filter((item) => !selectedRegion || item.regionName === selectedRegion)
        .map((item) => ({
        value: item.networkInterfaceArn,
        label: item.name,
        }));
    },
    mediaConnectOutputOptions() {
      return this.mediaConnectOutputs.map((output) => ({
        value: output.Id,
        label: output.Name ? `${output.Name} (${output.RegionName || output.Id})` : output.Id,
      }));
    },
  },
  methods: {
    emitModel(nextModel) {
      this.$emit("input", nextModel);
    },
    updateRoot(field, value) {
      this.emitModel({
        ...this.model,
        [field]: value,
      });
    },
    updateOutput(field, value) {
      this.emitModel({
        ...this.model,
        output: {
          ...this.model.output,
          [field]: value,
        },
      });
    },
  },
};
</script>
